/**
 * Where the PDF's pages end, estimated in the editor.
 *
 * The figures mirror trustedge/mou/pdf.py (PAGE_H, MARGIN_X, TOP_*, BOTTOM_*
 * and the body CSS) — keep them in step. The editor lays the text out at the
 * PDF's own width and type size (see PAGE_EDITOR_CSS), so a line here is close
 * to a line there; "Preview PDF" stays the exact answer.
 */

const PX_PER_CM = 96 / 2.54;

export const PAGE_TEXT_WIDTH_PX = Math.round((21 - 2 * 1.8) * PX_PER_CM);   // 658

// A header/footer that is one picture and nothing else (a letterhead) prints
// at the full text width and its band grows to fit — pdf.py _banner and
// _page_css. headerCm / footerCm are that picture's printed height.
export const TEXT_WIDTH_CM = 21 - 2 * 1.8;
export const BANNER_MAX_CM = 4.5;

export function pageMarginsCm({ customHeader = false, customFooter = false, headerCm = null, footerCm = null } = {}) {
  let top = customHeader ? 3.6 : 2.4;
  let bottom = customFooter ? 2.9 : 2.2;
  if (headerCm) top = Math.max(top, 0.8 + headerCm + 0.5 + 0.4);
  if (footerCm) bottom = Math.max(bottom, 0.6 + footerCm + 0.5 + 0.4);
  return { top, bottom };
}

export function pageHeightPx(geometry = {}) {
  const { top, bottom } = pageMarginsCm(geometry);
  return (29.7 - top - bottom) * PX_PER_CM;
}

export const cmToPx = (cm) => cm * PX_PER_CM;

/**
 * The printed height in cm of a banner header/footer — one image, no text —
 * or null when the band is anything else. Async: the image's own size is
 * needed, as in pdf.py.
 */
export function bannerHeightCm(html) {
  if (!html || typeof DOMParser === "undefined") return Promise.resolve(null);
  const doc = new DOMParser().parseFromString(html, "text/html");
  const imgs = doc.querySelectorAll("img");
  if (imgs.length !== 1 || (doc.body.textContent || "").replace(/ /g, "").trim()) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const im = new Image();
    im.onload = () => {
      if (!im.naturalWidth) return resolve(null);
      resolve(Math.min(TEXT_WIDTH_CM * im.naturalHeight / im.naturalWidth, BANNER_MAX_CM));
    };
    im.onerror = () => resolve(null);
    im.src = imgs[0].getAttribute("src");
  });
}

// Page 1 starts with the document title and the MOU No. / dates strip -
// 2.0 cm in the PDF (measured on MOU-2026-0004, 2026-10-15).
export const FIRST_PAGE_OFFSET_PX = 76;
// A {{signature_block}} is one line in the editor and a full two-party
// signature table in the PDF, about 4.5 cm tall.
export const SIGNATURE_BLOCK_EXTRA_PX = 144;

const PAGE_BREAK_RE = /^\{\{\s*page_break\s*\}\}$/;
const SIG_BLOCK_RE = /^\{\{\s*signature_block\s*\}\}$/;

/**
 * Page boundaries for the blocks inside a Quill root, in the root's content
 * coordinates (offsetTop of its children). Returns { breaks: [{ y, page }],
 * pages }. Text flows across a page boundary line by line, so a boundary can
 * fall inside a paragraph, as it does in the PDF.
 */
export function computePageBreaks(root, geometry) {
  const page = pageHeightPx(geometry);
  const breaks = [];
  if (!root) return { breaks, pages: 1 };

  const first = root.firstElementChild;
  const base = first ? first.offsetTop : 0;
  let pageEnd = base + page - FIRST_PAGE_OFFSET_PX;   // in PDF coordinates
  let shift = 0;                                      // PDF y minus editor y
  let n = 1;

  for (const el of root.children) {
    const text = (el.textContent || "").trim();
    const top = el.offsetTop;
    const marginBottom = parseFloat(getComputedStyle(el).marginBottom) || 0;
    const bottom = top + el.offsetHeight + marginBottom;
    const shiftAtTop = shift;
    if (SIG_BLOCK_RE.test(text)) shift += SIGNATURE_BLOCK_EXTRA_PX;

    if (PAGE_BREAK_RE.test(text)) {
      n += 1;
      breaks.push({ y: bottom, page: n, forced: true });
      pageEnd = bottom + shift + page;
      continue;
    }
    // A signature table is never split; it moves to the next page whole.
    if (SIG_BLOCK_RE.test(text) && bottom + shift > pageEnd) {
      n += 1;
      breaks.push({ y: top, page: n });
      pageEnd = top + shiftAtTop + page;
    }
    // A table breaks between rows, never inside one: a row that does not fit
    // moves to the next page whole, leaving the rest of this page empty.
    if (el.tagName === "TABLE") {
      for (const tr of el.querySelectorAll("tr")) {
        const rowTop = top + tr.offsetTop;
        const rowBottom = rowTop + tr.offsetHeight;
        if (rowBottom + shift > pageEnd) {
          n += 1;
          breaks.push({ y: rowTop, page: n });
          const gap = pageEnd - (rowTop + shift);
          if (gap > 0) shift += gap;
          pageEnd += page;
        }
      }
      continue;
    }
    while (bottom + shift > pageEnd) {
      n += 1;
      breaks.push({ y: pageEnd - shiftAtTop, page: n });
      pageEnd += page;
    }
  }
  return { breaks, pages: n };
}

/** The page a content-y position falls on. */
export function pageAt(y, breaks) {
  let p = 1;
  for (const b of breaks) if (y >= b.y) p = b.page;
  return p;
}

// Applied to the editor in page mode: the PDF's text width, type sizes and
// paragraph spacing (pdf.py _CSS), so line wrapping — and therefore page
// estimates — match.
export const PAGE_EDITOR_CSS = `
.mou-page-editor .ql-editor {
  width: ${PAGE_TEXT_WIDTH_PX + 32}px; max-width: none; margin: 0 auto;
  padding: 16px; font-size: 13.33px; line-height: 1.45; color: #1e293b;
  font-family: Arial, "DejaVu Sans", sans-serif;
}
.mou-page-editor .ql-editor p { margin: 0 0 8px 0; }
.mou-page-editor .ql-editor h1 { font-size: 22.7px; margin: 0 0 5px 0; }
.mou-page-editor .ql-editor h2 { font-size: 17.3px; margin: 16px 0 5px 0; }
.mou-page-editor .ql-editor h3 { font-size: 14.7px; margin: 13px 0 4px 0; }
.mou-page-editor .ql-editor li { margin: 0 0 2.7px 0; }
.mou-page-editor .ql-editor table td { border: 1px solid #94a3b8; padding: 7px 12px 8px; }
/* xhtml2pdf spacing the browser does not reproduce on its own, calibrated
   against the real PDF of MOU-2026-0004 (within ~0.05 cm per section):
   ~2pt more leading under every block; lists keep their 6pt bottom margin;
   and margins add up rather than collapse where a list meets a heading or a
   nested list ends. */
.mou-page-editor .ql-editor p, .mou-page-editor .ql-editor li { padding-bottom: 2.67px; }
.mou-page-editor .ql-editor h1, .mou-page-editor .ql-editor h2,
.mou-page-editor .ql-editor h3 { padding-bottom: 5px; }
.mou-page-editor .ql-editor ol, .mou-page-editor .ql-editor ul { margin: 0 0 8px 0; }
.mou-page-editor .ql-editor ol + h2, .mou-page-editor .ql-editor ul + h2 { margin-top: 24px; }
.mou-page-editor .ql-editor ol + h3, .mou-page-editor .ql-editor ul + h3 { margin-top: 21px; }
.mou-page-editor .ql-editor ol + h1, .mou-page-editor .ql-editor ul + h1 { margin-top: 8px; }
.mou-page-editor .ql-editor li[class*="ql-indent-"] + li:not([class*="ql-indent-"]) { margin-top: 8px; }
.mou-band { width: ${PAGE_TEXT_WIDTH_PX + 32}px; margin: 0 auto; background: #fff; padding: 6px 16px;
  font-family: Arial, "DejaVu Sans", sans-serif; font-size: 11.3px; color: #334155; position: relative; }
.mou-band p { margin: 0; }
.mou-band img { max-height: 42px; }
.mou-band.is-banner img { width: 100%; max-height: none; display: block; }
.mou-band table { width: 100%; }
.mou-band-head { border-bottom: 1px dashed #c7d2fe; }
.mou-band-foot { border-top: 1px dashed #c7d2fe; }
.mou-band-tag { position: absolute; right: 4px; top: 2px; font-size: 9px; letter-spacing: .04em;
  text-transform: uppercase; color: #6366f1; background: rgba(238,242,255,.9); padding: 0 4px; border-radius: 3px; }
`;
